(ns dre.panels.author.events
  (:require
   [clojure.string :as str]
   [clojure.set :as set]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.network :as-alias network]
   [dre.pages.search.events :as search-events]
   [dre.shared.interface.search.pills :as pills]
   [dre.shared.interface.author.core :as author]
   [dre.utils.query :as q]
   [dre.utils :refer [stringify-keys-with-namespaces]]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

(def db-path [:panels :data :author])

(def wait-for
  (zipmap [:details-loading?
           :cooccurrences-loading?
           :bibliography-loading?]
          (repeat true)))

(defn disambiguation-query
  [search-query author]
  (let [author-lookup (get-in author [:author/author :author/lookup])
        query-blueprints (:query/pills search-query)]
    (pills/disambiguated-author-pill
     author-lookup
     (pills/flats->pill query-blueprints))))

(defn mlower
  [s]
  (when-not (str/blank? s)
    (str/lower-case s)))

(def orcid-domain "orcid.org")
(def orcid-uri (str "https://" orcid-domain "/"))
(defn remove-orcid-uri
  [s]
  (str/replace s (re-pattern (str "https?://" orcid-domain "/")) ""))

(defn get-orcid
  [ids]
  (first (distinct
          (map (comp remove-orcid-uri :id/name)
               (filter #(= (:id/source %) "ORCID") ids)))))

(defn keep-longer-author-name
  [author-left author-right]
  (let [name-count (fn [author]
                     (let [an (:author/name author)]
                       (+ (transduce (map count)
                                     +
                                     (:author/given-names an))
                          (transduce (map count)
                                     +
                                     (:author/surnames an)))))
        left-count (name-count author-left)
        right-count (name-count author-right)]
    (if (< left-count right-count)
      author-right
      author-left)))

(defn coauthors-from-articles
  [articles author-lookup]
  (let [author-lookup-group (author/lookup-group author-lookup)

        ;; frequencies of coauthors
        ;; key each author by its lookup-group (~ lastname + initial)
        coauthors
        (transduce
         (comp (mapcat :article/persons)
               (map #(select-keys % [:author/lookup :author/name]))
               (map (fn [author]
                      (assoc author
                             :author/lookup-group
                             (author/lookup-group (:author/lookup author)))))
               ;; remove the root author
               (remove (fn [author]
                         (= (:author/lookup-group author)
                            author-lookup-group))))
         (fn
           ([acc]
            (->> acc
                 (sort-by (fn [[_lookup [_author freq]]] freq) >)
                 (into []
                       (comp
                        (map (fn [[_lookup [author freq]]]
                               (assoc author :author/coauthor-freq freq)))
                        (take 20)))))
           ([acc author]
            (update acc (:author/lookup-group author)
                    (fn [[a c :as entry]]
                      (if-not entry
                        [author 1]
                        [(keep-longer-author-name a author) (inc c)])))))
         {}
         articles)]
    coauthors))

;; TODO use from shared interface
(defn author-affiliations-from-articles
  [articles author-lookup]
  (let [mf (fn [article]
             (let [{:keys [:article/persons :article/published]} article
                   author (some #(when (= author-lookup
                                          (:author/lookup %))
                                   %)
                                persons)]
               (when author
                 (assoc author :article/published (js/Date. published)))))
        authors (keep mf articles)
        af (fn [author]
             (map #(assoc % :article/author author)
                  (:author/affiliations author)))
        affiliations (mapcat af authors)]
    (author/author-affiliations affiliations)))
;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;;events

(rf/reg-event-fx
 ::init
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [params search-query]}]]
  (let [sq (or (:query/article-context params) search-query)
        dq (disambiguation-query sq params)
        search-query {:query/pills
                      (pills/provide-pills dq)}
        search-query (q/pills-api-params search-query)
        author-lookup (:pill/query dq)
        max-requests 10]
    {:db {:state wait-for
          :params params}
     :fx [[:dispatch [::fetch-bibliography search-query]]
          [:dispatch [::fetch-cooccurrences search-query 0 max-requests]]
          [:dispatch [::fetch-details params author-lookup dq search-query]]]}))) 


;; TODO migrate to the new articles api
(rf/reg-event-fx
 ::fetch-details
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} [params author-lookup disambiguation-query search-query]]
   {::network/request [{:endpoint :search/articles-old}
                       {:body (stringify-keys-with-namespaces (assoc search-query
                                                                     :authors? true
                                                                     :articles? true
                                                                     :author-lookup author-lookup
                                                                     :limit 250))
                        :on-success [::fetch-details-success {:params params 
                                                              :disambiguation-query disambiguation-query}]}]}))

(rf/reg-event-fx
 ::fetch-details-success
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [params disambiguation-query ::fetch-fx/response]}]]
  (let [{:keys [:article/authors :article/articles]} (:body response)
        author-lookup (:pill/query disambiguation-query)
        affiliations (author-affiliations-from-articles
                      articles author-lookup)
        coauthors (coauthors-from-articles articles author-lookup)
        emails
        (sort (distinct (map mlower
                             (mapcat :author/emails authors))))
        ids (distinct (keep :author/id authors))
        orcid (get-orcid ids)
        details {:details-loading? false
                 :author/affiliations affiliations
                 :author/emails emails
                 :author/ids ids
                 :author/orcid orcid
                 :author/coauthors coauthors}]
    {:db (-> db
             (assoc-in [:state :details-loading?] false)
             (assoc-in [:data :details] details))})))

(rf/reg-event-fx
 ::fetch-cooccurrences
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} [search-query attempt max-requests]]
   (let []
     {::network/request [{:endpoint :widgets/top-terms}
                         {:body (stringify-keys-with-namespaces (merge search-query
                                                                       {:types ["I" "ALL"]
                                                                        :k 5}))
                          :on-success [::fetch-cooccurrences-success {:search-query search-query
                                                                      :max-requests max-requests
                                                                      :attempt attempt}]}]})))

(rf/reg-event-fx
 ::fetch-cooccurrences-success
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [search-query max-requests attempt ::fetch-fx/response]}]]
  (let [response-body (:body response)
        done? (:done? response-body)
        interests (not-empty (get-in response-body [:top (keyword "ALL")]))
        interventions (not-empty (get-in response-body [:top (keyword "I")]))]
    (if (and (not done?) (> max-requests attempt))
      {:dispatch [::fetch-cooccurrences search-query (inc attempt) max-requests]}
      {:db (-> db
               (assoc-in [:state :cooccurrences-loading?] (and (not (:done? response-body))
                                                               (< attempt max-requests)))
               (assoc-in [:data :author] {:interests interests
                                          :interventions interventions}))}))))

;; TODO dont fetch highlights + authors + others
(rf/reg-event-fx
 ::fetch-bibliography
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} [search-query]]
   (let [params {:query (:query/pill search-query)
                 :limit 15
                 :offset 0
                 :order "published-desc"}]
     {::network/request [{:endpoint :search/articles}
                         {:body (stringify-keys-with-namespaces params)
                          :on-success [::fetch-bibliography-success]}]})))


(rf/reg-event-fx
 ::fetch-bibliography-success
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (let []
    {:db (-> db
             (assoc-in [:state :bibliography-loading?] false)
             (assoc-in [:data :bibliography] {:author/bibliography (:articles (:body response))}))})))


;;subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (get-in db db-path)))